<template>
  <v-container v-if="gotData">
    <v-layout column align-center justify-center>
      <v-flex class="ma-2 mb-4 text-xs-center">
        <div class="">
          {{ time | moment('dddd, MMMM D') }}
        </div>
        <div class="display-1">
          {{ time | moment('h:mm:ss a') }}
        </div>

        <div class="shift-current-status">
          <div v-if="!shiftStarted" class="red--text">
            <h1 class="display-1">Shift Not Yet Started</h1>
          </div>

          <div v-if="currentlyIn" class="success--text pulsate">
            <h1 class="display-1">Clocked In</h1>
          </div>

          <div v-if="!currentlyIn && shiftStarted && !shiftEnded" class="red--text pulsate">
            <h1 class="display-1">On Break</h1>
          </div>

          <div v-if="shiftEnded" class="red--text">
            <h1 class="display-1">Shift Ended</h1>
          </div>
        </div>

      </v-flex>

      <v-flex class="grey lighten-3 pa-3">
        <h1 class="text-xs-center">Welcome, {{ user.display_name }}!</h1>

        <v-layout row wrap justify-space-between class="mt-5">
          <v-flex class="pa-4">
            <h2
              class="subtle font-weight-bold"
            >
              Current Shift
            </h2>
            <div>
              <div v-for="punch in sortedPunches">
                <ul class="info-listing">
                  <li v-if="punch.start">
                    <span>Time In:</span> {{ punch.punchTime | moment("h:mm a") }}
                    <span v-if="punch.startingPunch">(Shift Started)</span>
                  </li>
                  <li v-else class="mb-2">
                    <span>Time Out:</span> {{ punch.punchTime | moment("h:mm a") }}
                    <span v-if="punch.endingPunch">(Shift Ended)</span>
                  </li>
                </ul>
              </div>
            </div>
          </v-flex>

          <v-flex class="px-4">
            <v-layout column>
              <div>
                <ShiftItem
                  :itemData="currentUserClockableShift"
                />
              </div>
            </v-layout>
          </v-flex>
        </v-layout>

        <div v-if="!shiftEnded">
          <div v-if="!shiftStarted">
            <v-dialog full-width v-if="tooEarly" v-model="startShiftOverrideDialog" max-width="500">
              <v-btn
                slot='activator'
                block
                color="success"
                class="mt-5"
              >
                <v-icon left>
                  timer
                </v-icon>
                Start Shift
              </v-btn>

              <v-card>
                <v-card-title class="headline red--text">Scheduled Time Conflict</v-card-title>
                <v-card-text>
                  <p>Based upon your currently scheduled shift you are attempting to clock in earlier than within the allowed 10 minute time window. Please try again later.</p>
                  <p class="mb-0 text-xs-center all-caps font-weight-bold">Client Override</p>
                  <p class="text-xs-center">Allow supervisor to clock in employee now</p>
                </v-card-text>
                <v-card-actions>
                  <v-layout column>
                    <v-flex>
                      <v-layout row justify-space-between>
                        <v-text-field
                          class="mr-2"
                          label="Username"
                          v-model="overrideUsername"
                          :error-messages="overrideError"
                        ></v-text-field>
                        <v-text-field
                          type="password"
                          label="Password"
                          v-model="overridePassword"
                          :error-messages="overrideError"
                        ></v-text-field>
                      </v-layout>
                    </v-flex>
                    <v-flex align-self-end>
                      <v-btn color="grey" flat @click="startShiftOverrideDialog = false">Nevermind</v-btn>
                      <v-btn color='success' small :disabled="apiCallPending" :loading="apiCallPending" @click="startShiftWithOverride">
                        Override
                      </v-btn>
                    </v-flex>
                  </v-layout>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <v-dialog v-else full-width v-model="startShiftDialog" max-width="290">
              <v-btn
                slot='activator'
                block
                color="success"
                class="mt-5"
              >
                <v-icon left>
                  timer
                </v-icon>
                Start Shift
              </v-btn>

              <v-card>
                <v-card-title class="headline">Starting Shift...</v-card-title>
                <v-card-text>
                  <p class="body-1">You will be clocked in and immediately logged out</p>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="grey" flat @click="startShiftDialog = false">Nevermind</v-btn>
                  <v-btn color='success' small :disabled="apiCallPending" :loading="apiCallPending" @click="startShift">
                    Clock In!
                  </v-btn>
              </v-card-actions>
              </v-card>
            </v-dialog>
          </div>

          <div v-else>
            <div v-if="currentlyIn">
              <v-btn
                block
                color="warning"
                class="mt-5"
                @click="clockOut"
                :disabled="apiCallPending"
                :loading="apiCallPending"
              >
                <v-icon left>
                  timer
                </v-icon>
                Pause Clock / Start Break
              </v-btn>
            </div>
            <div v-else>
              <v-btn
                block
                color="success"
                class="mt-5"
                @click="clockIn"
                :disabled="apiCallPending"
                :loading="apiCallPending"
              >
                <v-icon left>
                  timer
                </v-icon>
                Resume Clock / End Break
              </v-btn>
            </div>

            <div class="end-shift-toggle text-xs-center mt-4">
              <a href="#" v-show="!showEndShift" @click.prevent="showEndShift = true">End Shift</a>
              <a href="#" v-show="showEndShift" @click.prevent="showEndShift = false">Hide</a>
            </div>

            <div v-if="showEndShift">
              <v-dialog full-width v-model="endShiftDialog" max-width="290">
              <v-btn
                slot='activator'
                block
                color="error"
                class="mt-3"
              >
                <v-icon left>
                  timer
                </v-icon>
                End Shift
              </v-btn>

                <v-card>
                  <v-card-title class="headline">Ending Shift...</v-card-title>
                  <v-card-text>
                    <p>This will clock you out finally, ending this shift. This cannot be undone</p>
                    <p>You will be logged out directly after.</p>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="grey" flat @click="endShiftDialog = false">Nevermind</v-btn>
                    <v-btn color='error' small :disabled="apiCallPending" :loading="apiCallPending" @click="endShift">
                      Clock Out!
                    </v-btn>
                </v-card-actions>
                </v-card>
              </v-dialog>
            </div>
          </div>
        </div>


        <div class="mt-4">
          <router-link :to="{ name: 'timeclockShiftConfirm' }">
            Back
          </router-link>
        </div>
      </v-flex>

    </v-layout>
  </v-container>
</template>

<script>
import { mapState } from 'vuex'
import { mapGetters } from 'vuex'
import ShiftItem from '@/components/shifts/timeclockShift.vue'

export default {
  components: {
    ShiftItem
  },
  computed: {
    ...mapState('userAuth', [
      'user',
    ]),
    ...mapGetters('shifts', [
      'currentUserClockableShift',
    ]),
    ...mapState('timeclock', [
      'punches'
    ]),
    ...mapGetters('timeclock', [
      'sortedPunches',
    ]),
    currentlyIn () {
      return this.$store.getters['timeclock/currentStatus'] == 'in' ? true : false
    },
    shiftStarted () {
      if (this.punches.filter(p => p.startingPunch).length > 0) {
        return true
      }
      return false
    },
    shiftEnded () {
      if (this.punches.filter(p => p.endingPunch).length > 0) {
        return true
      }
      return false
    },
    tooEarly () {
      const now = new this.$moment()
      const shiftStart = this.$moment(this.currentUserClockableShift.startDatetime)
      if (now.isBefore(shiftStart)) {
        const duration = this.$moment.duration(shiftStart.diff(now))
        if (duration.minutes() > 10) {
          return true
        }
        return false
      }
      return false
    },
    tooLate () {
      const now = new this.$moment()
      const shiftEnd = this.$moment(this.currentUserClockableShift.endDatetime)
      if (now.isAfter(shiftEnd)) {
        const duration = this.$moment.duration(now.diff(shiftEnd))
        if (duration.minutes() > 10) {
          return true
        }
        return false
      }
      return false
    },
  },
  watch: {
    'user': 'fetchData'
  },
  data: function () {
    return {
      time: Date.now(),
      apiCallPending: false,
      showEndShift: false,
      startShiftDialog: false,
      startShiftOverrideDialog: false,
      endShiftDialog: false,
      endShiftOverrideDialog: false,
      overrideUsername: '',
      overridePassword: '',
      overrideError: null,
      gotData: false,
    }
  },
  created () {
    this.tickTime()
    this.fetchData(this.user)
  },
  methods: {
    checkForUser () {
      console.log('user?')
      return this.user
    },
    updateTime () {
      this.time = Date.now()
    },
    tickTime() {
      var timerId = setInterval(this.updateTime, 1000)
      this.updateTime()
    },
    fetchData (user) {
      if (user.id) {
        this.$store.dispatch('shifts/getCurrentUserUpcoming', this.user.id)
          .then(res => {
            const shiftId = this.currentUserClockableShift.id
            this.$store.dispatch('timeclock/getPunches', { shiftId: shiftId })
              .then(res => {
                this.gotData = true
              })
          })
      } else {
        return
      }
    },
    startShift () {
      this.apiCallPending = true
      const punchTime = Date.now()
      const userId = this.user.id
      const shiftId = this.currentUserClockableShift.id
      this.$store.dispatch(
        'timeclock/clockIn',
        {
          userId: userId,
          shiftId: shiftId,
          punchTime: punchTime,
          endcap: true,
        }
      )
      .then(res => {
        this.$store.dispatch('userAuth/logout')
      })
      .finally(res => {
        this.apiCallPending = false
      })
    },
    startShiftWithOverride () {
      this.apiCallPending = true
      const punchTime = Date.now()
      const userId = this.user.id
      const shiftId = this.currentUserClockableShift.id
      this.$store.dispatch(
        'timeclock/clockInOverride',
        {
          userId: userId,
          shiftId: shiftId,
          punchTime: punchTime,
          endcap: true,
          overrideUsername: this.overrideUsername,
          overridePassword: this.overridePassword,
        }
      ).catch(err => {
        this.overrideError = "That didn't work"
      })
      .then(res => {
        this.$store.dispatch('userAuth/logout')
      })
      .finally(res => {
        this.apiCallPending = false
      })
    },
    clockIn () {
      this.apiCallPending = true
      const punchTime = Date.now()
      const userId = this.user.id
      const shiftId = this.currentUserClockableShift.id
      this.$store.dispatch(
        'timeclock/clockIn',
        {
          userId: userId,
          shiftId: shiftId,
          punchTime: punchTime,
          endcap: false,
        }
      )
      .then(res => {
        this.$store.dispatch('userAuth/logout')
      })
      .finally(res => {
        this.apiCallPending = false
      })
    },
    clockOut () {
      this.apiCallPending = true
      const punchTime = Date.now()
      const userId = this.user.id
      const shiftId = this.currentUserClockableShift.id
      this.$store.dispatch(
        'timeclock/clockOut',
        {
          userId: userId,
          shiftId: shiftId,
          punchTime: punchTime,
          endcap: false,
        }
      )
      .then(res => {
        this.$store.dispatch('userAuth/logout')
      })
      .finally(res => {
        this.apiCallPending = false
      })
    },
    endShift (needsOverride) {
      this.apiCallPending = true
      const punchTime = Date.now()
      const userId = this.user.id
      const shiftId = this.currentUserClockableShift.id
      this.$store.dispatch(
        'timeclock/clockOut',
        {
          userId: userId,
          shiftId: shiftId,
          punchTime: punchTime,
          endcap: true,
          needsOverride: needsOverride
        }
      )
      .then(res => {
        this.$store.dispatch('userAuth/logout')
      })
      .finally(res => {
        this.apiCallPending = false
      })
    },

    endShiftWithOverride () {
      this.apiCallPending = true
      const punchTime = Date.now()
      const userId = this.user.id
      const shiftId = this.currentUserClockableShift.id
      this.$store.dispatch(
        'timeclock/clockOutOverride',
        {
          userId: userId,
          shiftId: shiftId,
          punchTime: punchTime,
          endcap: true,
          overrideUsername: this.overrideUsername,
          overridePassword: this.overridePassword,
        }
      )
      .then(res => {
        this.$store.dispatch('userAuth/logout')
      })
      .catch(err => {
        this.overrideError = "That didn't work"
      }).finally(res => {
        this.apiCallPending = false
      })
    },
  },
}
</script>

<style scoped>
#dashboard-container {
}

</style>
